import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Moment } from 'moment-timezone';
import {
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    createContext,
    useContext,
    useMemo,
    useState,
} from 'react';
import { useLocation } from 'react-router';

import { VisitState } from '../../types/visit';
import { useQueryParams } from '../../utils/queryParams';
import {
    AddressForVisitBookingFlow,
    useAddressForVisitBookingFlow,
} from './useAddressForVisitBookingFlow';
import {
    VisitRequestForVisitBookingFlow,
    useVisitRequestForVisitBookingFlow,
} from './useVisitRequestForVisitBookingFlow';

export const VISIT_BOOKING_INFO_PAGE = 'info';
export const VISIT_BOOKING_SCHEDULING_PAGE = 'scheduling';

const PATIENT_ORGANIZATION_QUERY = gql`
    query PatientOrganization($id: UUID!) {
        patient(id: $id) {
            id
            organization {
                id
            }
        }
    }
`;

type VisitBookingPageContextType = {
    patientId: string;
    demandOrgId: string;
    address: AddressForVisitBookingFlow | null;
    serviceAreaId: string;
    serviceBundleId: string;
    visitRequest: VisitRequestForVisitBookingFlow | null;
    selectedTab: VisitState.CONFIRMED | VisitState.UNSCHEDULED;
    setSelectedTab: Dispatch<SetStateAction<VisitState.CONFIRMED | VisitState.UNSCHEDULED>>;
    isSchedulingPage: boolean;
    selectedDate: Moment | null;
    setSelectedDate: Dispatch<SetStateAction<Moment | null>>;
    timezone: string;
    setTimezone: Dispatch<SetStateAction<string>>;
    availableClinicianIds: string[];
    setAvailableClinicianIds: Dispatch<SetStateAction<string[]>>;
};
const VisitBookingPageContext = createContext<VisitBookingPageContextType | undefined>(undefined);

export const VisitBookingPageProvider = ({ children }: PropsWithChildren) => {
    // Atoms for address, service area, and service bundle selection so that they persist
    // across page reloads and don't kick the user back to the info page if they're already
    // on the scheduling page.
    const [{ patientId }] = useQueryParams({ patientId: '' });
    const [{ serviceAreaId }] = useQueryParams({ serviceAreaId: '' });
    const [{ serviceBundleId }] = useQueryParams({ serviceBundleId: '' });
    const { data } = useQuery<{
        patient: {
            organization: { id: string };
        } | null;
    }>(PATIENT_ORGANIZATION_QUERY, { variables: { id: patientId } });
    const demandOrgId = data?.patient?.organization.id ?? '';
    const address = useAddressForVisitBookingFlow();
    const visitRequest = useVisitRequestForVisitBookingFlow();

    const [selectedTab, setSelectedTab] = useState<VisitState.CONFIRMED | VisitState.UNSCHEDULED>(
        VisitState.CONFIRMED,
    );
    const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
    const [timezone, setTimezone] = useState<string>('');
    const [availableClinicianIds, setAvailableClinicianIds] = useState<string[]>([]);

    const location = useLocation();
    const isSchedulingPage = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const addressId = params.get('addressId');
        const visitRequestId = params.get('visitRequestId');
        return (
            !!patientId && !!serviceAreaId && !!addressId && (!!serviceBundleId || !!visitRequestId)
        );
    }, [patientId, serviceAreaId, serviceBundleId, location.search]);

    return (
        <VisitBookingPageContext.Provider
            value={{
                address,
                patientId,
                demandOrgId,
                serviceAreaId,
                serviceBundleId,
                visitRequest,
                selectedTab,
                setSelectedTab,
                isSchedulingPage,
                selectedDate,
                setSelectedDate,
                timezone,
                setTimezone,
                availableClinicianIds,
                setAvailableClinicianIds,
            }}
        >
            {children}
        </VisitBookingPageContext.Provider>
    );
};

export const useVisitBookingPageContext = () => {
    const context = useContext(VisitBookingPageContext);
    if (context === undefined) {
        throw new Error(
            'useVisitBookingPageContext must be used within a VisitBookingPageProvider',
        );
    }
    return context;
};
